<template>
	<app-page>
		<app-heading :title="isNew ? 'New Website' : website.name" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
					<app-button type="submit" small color="primary" v-else>Save</app-button>
					<app-button type="cancel" small :to="{ name: 'websites' }">Cancel</app-button>
				</app-section-actions>

				<div class="title">Basic informations</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							label="Name"
							v-model="website.name"
							required
							:errors="errors.name"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							label="Slug"
							v-model="website.slug"
							required
							:errors="errors.slug"
							:disabled="!isNew"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.domain"
							label="Domain"
							:required="true"
							:errors="errors.domain"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-select
							v-model="website.enterprise"
							:errors="errors.enterprise"
							:items="enterprises"
							label="Enterprise"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-select
							v-model="website.lead_resort"
							:errors="errors.lead_resort"
							:disabled="!website.enterprise"
							:items="resorts"
							label="Send leads to Resort"
							clearable
							hint="Leave empty on multi-resort brand sites. Unit and complex inquiries go to that property."
							persistent-hint
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-select
							v-model="website.template"
							:errors="errors.template"
							:items="templates"
							:disabled="!isNew && !!website.template"
							label="Templates"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">License & Registration</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.require_license"
							label="Enforce Units To Have License Number"
							:errors="errors.require_license"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.license_name"
							label="License Name"
							hint="Example: CITQ <LICENSE>"
							:required="true"
							:errors="errors.license_name"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.require_registration"
							label="Enforce Units To Have Registration Number"
							:errors="errors.require_registration"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.registration_name"
							label="Registration Name"
							hint="Example: registation number <REGISTRATION>"
							:required="true"
							:errors="errors.registration_name"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Localization & Currency</div>
				<app-row>
					<app-cell cols="12" sm="3">
						<input-locale
							label="Locales"
							v-model="website.locales"
							required
							multiple
							:errors="errors.locales"
							:preferred="['en','fr']"
						/>
					</app-cell>
					<app-cell cols="12" sm="3">
						<input-select
							v-model="website.currency"
							:items="primaryCurrencies"
							:errors="errors.currency"
							label="Primary Currency"
						/>
					</app-cell>
					<app-cell cols="12" sm="3">
						<input-select
							v-model="website.supported_currencies"
							multiple
							:items="currencies"
							:errors="errors.supported_currencies"
							label="Supported Currencies"
						/>
					</app-cell>
					<app-cell cols="12" sm="3">
							<input-timezone
								v-model="website.timezone"
								label="Timezone"
								:errors="errors.timezone"
							/>
						</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Style</div>
				<app-row>
					<app-cell cols="12" sm="6">
						<input-color-picker
							v-model="website.primary_color"
							label="Primary color"
						/>
					</app-cell>
					<app-cell cols="12" sm="6">
						<input-color-picker
							v-model="website.secondary_color"
							label="Secondary color"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-file
							v-model="favicon"
							label="Favicon"
							type="image"
							image
							:image-placeholder="website.favicon"
							@load="onImageLoad('favicon', $event)"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-file
							label="Logo (for white/light background)"
							type="image"
							image
							image-width="280"
							image-height="128"
							:image-placeholder="website.logo_light"
							@load="onImageLoad('logo_light', $event)"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-file
							label="Logo (for black/dark background)"
							type="image"
							image
							image-dark
							image-width="280"
							image-height="128"
							:image-placeholder="website.logo_dark"
							@load="onImageLoad('logo_dark', $event)"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Contact informations</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.phone"
							label="Phone number (Canada & USA)"
							:errors="errors.phone"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.phone_worldwide"
							label="Phone number (Worldwide)"
							:errors="errors.phone_worldwide"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="email"
							v-model="website.contact_email"
							label="Contact email"
							:errors="errors.contact_email"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.address"
							label="Address"
							:errors="errors.address"
						/>
					</app-cell>
					<app-cell cols="12" sm="2">
						<input-text
								v-model="website.city"
								label="City"
								:errors="errors.city"
							/>
					</app-cell>
					<app-cell cols="12" sm="2">
						<input-text
							v-model="website.state"
							label="State"
							:errors="errors.state"
						/>
					</app-cell>
					<app-cell cols="12" sm="2">
						<input-text
							v-model="website.zip_code"
							label="Zip/Postal Code"
							:errors="errors.zip_code"
						/>
					</app-cell>
					<app-cell cols="12" sm="2">
						<input-text
							v-model="website.country"
							label="Country"
							:errors="errors.country"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Booking</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							v-model="website.booking_age"
							label="Booking consent age"
							:errors="errors.booking_age"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.sameday_booking"
							label="Sameday Booking"
							:errors="errors.sameday_booking"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-time-picker
							v-model="website.sameday_time"
							label="Sameday Booking Time"
							color="primary"
							step="15"
							:disabled="!website.sameday_booking"
							:errors="errors.sameday_time"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Consent</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.consent_form"
							label="Enable Consent Form"
							:errors="errors.consent_form"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.contact_form_sms_opt_in"
							label="Enable transactional SMS opt-in"
							:errors="errors.contact_form_sms_opt_in"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Featured Amenities</div>
				<app-row>
					<app-cell cols="12" sm="3">
						<input-switch
							v-model="website.show_featured_amenities"
							label="Enabled"
							:errors="errors.show_featured_amenities"
						/>
					</app-cell>
					<app-cell cols="12" sm="6">
						<input-combobox
							v-model="website.featured_amenities"
							:items="features"
							:disabled="!website.show_featured_amenities"
							:errors="errors.featured_amenities"
							chips
							multiple
							label="Features"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Advanced Search</div>
				<app-row>
					<app-cell cols="12" sm="3">
						<input-switch
							v-model="website.advanced_search"
							label="Enabled"
							:errors="errors.advanced_search"
						/>
					</app-cell>
					<app-cell cols="12" sm="3">
						<input-switch
							v-model="website.advanced_search_pets"
							label="Pets Allowed Feature"
							:errors="errors.advanced_search_pets"
						/>
					</app-cell>
					<app-cell cols="12" sm="6">
						<input-combobox
							v-model="website.advanced_search_features"
							:items="features"
							:disabled="!website.advanced_search"
							:errors="errors.advanced_search_features"
							chips
							multiple
							label="Features"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Social Media</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.facebook"
							label="Facebook"
							:errors="errors.social_media && errors.social_media.facebook"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.youtube"
							label="Youtube"
							:errors="errors.social_media && errors.social_media.youtube"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.google_business"
							label="Google My Business"
							:errors="errors.social_media && errors.social_media.google_business"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.instagram"
							label="Instagram"
							:errors="errors.social_media && errors.social_media.instagram"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.yelp"
							label="Yelp"
							:errors="errors.social_media && errors.social_media.yelp"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-text
							type="url"
							v-model="website.social_media.tiktok"
							label="TikTok"
							:errors="errors.social_media && errors.social_media.tiktok"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">API keys</div>
				<app-row>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.gmap_key"
							label="Google Maps API Key"
							:errors="errors.gmap_key"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.gtm_key"
							label="Google Tag Manager API Key"
							:errors="errors.gtm_key"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.recaptcha_key"
							label="Recaptcha (V3) API Key"
							:errors="errors.recaptcha_key"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.recaptcha_secret"
							label="Recaptcha (V3) Secret Key"
							:errors="errors.recaptcha_secret"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.campaigner_key"
							label="Campaigner API Key"
							:errors="errors.campaigner_key"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Affirm</div>
				<app-row>
					<app-cell cols="12" sm="3">
						<input-switch
							v-model="website.affirm_enable"
							label="Enabled"
							:errors="errors.affirm_enable"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.affirm_public_key"
							label="Affirm Public Key"
							:errors="errors.affirm_public_key"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.affirm_private_key"
							label="Affirm Private Key"
							:errors="errors.affirm_private_key"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							v-model="website.affirm_country"
							label="Affirm Country (Canada / United States)"
							:errors="errors.affirm_country"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">SEO</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-switch
							v-model="website.disallow_bots"
							label="Disallow bots"
						/>
					</app-cell>
					<app-cell cols="12" sm="4">
						<input-file
							label="OG Image"
							type="image"
							image
							image-width="280"
							image-height="128"
							:image-placeholder="website.og_image"
							@load="onImageLoad('og_image', $event)"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Tracking</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-text
							v-model="website.facebook_pixel_id"
							label="Facebook Pixel ID"
							:errors="errors.facebook_pixel_id"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							v-model="website.googleads_customer_id"
							label="Google Ads Customer ID"
							:errors="errors.googleads_customer_id"
						/>
					</app-cell>
				</app-row>

				<app-divider class="ma-4" />
				<div class="title">Custom CSS</div>
				<input-textarea
					v-model="website.custom_style"
					outlined
					rows="20"
					style="font-size: 12px;"
				/>
			</app-section>
		</app-form>
	</app-page>
</template>

<script>
export default {
	data() {
		return {
			website: {
				name: '',
				slug: '',
				domain: '',
				template: null,
				locales: [],
				currency: null,
				supported_currencies: ['USD', 'CAD', 'EUR'],
				lead_resort: null,
				timezone: null,
				phone: '',
				phone_worldwide: '',
				address: '',
				city: '',
				state: '',
				zip_code: '',
				country: '',
				contact_email: '',
				primary_color: '',
				secondary_color: '',
				license_name: '#',
				registration_name: '#',
				require_license: false,
				require_registration: false,
				booking_age: 25,
				sameday_booking: false,
				sameday_time: '00:00',
				show_featured_amenities: false,
				consent_form: false,
				contact_form_sms_opt_in: false,
				featured_amenities: null,
				advanced_search: false,
				advanced_search_pets: false,
				advanced_search_features: null,
				facebook_pixel_id: null,
				googleads_customer_id: null,
				favicon: null,
				logo_light: null,
				logo_dark: null,
				gmap_key: null,
				gtm_key: null,
				recaptcha_key: null,
				recaptcha_secret: null,
				campaigner_key: null,
				custom_style: '',
				disallow_bots: false,
				og_image: null,
				social_media: {
					facebook: '',
					youtube: '',
					google_business: '',
					instagram: '',
					yelp: '',
					tiktok: '',
				},
				affirm_enable: null,
				affirm_public_key: null,
				affirm_private_key: null,
				affirm_country: null,
			},
			templates: [],
			resorts: [],
			enterprises: [],
			features: [],
			errors: {},
			logo_light: null,
			logo_dark: null,
			og_image: null,
			favicon: null,

			primaryCurrencies: [{
				text: 'US Dollars',
				value: 'USD',
			}, {
				text: 'Canadian Dollars',
				value: 'CAD',
			}],
			currencies: [],
		}
	},
	computed: {
		id() {
			return this.$route.params.website
		},
		isNew() {
			return this.id === 'new'
		},
	},
	watch: {
		'website.enterprise': {
			handler() {
				this.fetchResortsDatasource()
			},
		},
	},
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			const datasources = await this.$ravello.graphql('websitetemplates')
				.sort('name')
				.datasource()
				.union(this.$ravello.graphql('enterprises').sort('name').datasource())
				.union(this.$ravello.graphql('features').sort('name').datasource())
				.query(true)

			this.templates = datasources.websitetemplates
			this.enterprises = datasources.enterprises
			this.features = datasources.features

			await this.fetchCurrencies()

			if (this.id !== 'new') {
				this.website = await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.id)
					.get()
				if (!this.website.social_media) {
					this.website.social_media = {}
				}
			}
		} catch (err) {
			this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
		}
		this.$store.dispatch('app/stopLoading')
	},
	methods: {
		onImageLoad(field, data) {
			this[field] = data
		},

		getRequestPayload() {
			return {
				name: this.website.name,
				domain: this.website.domain,
				currency: this.website.currency,
				supported_currencies: this.website.supported_currencies,
				timezone: this.website.timezone,
				enterprise: this.website.enterprise,
				lead_resort: this.website.lead_resort || null,
				template: this.website.template,
				phone: this.website.phone,
				phone_worldwide: this.website.phone_worldwide,
				address: this.website.address,
				license_name: this.website.license_name,
				registration_name: this.website.registration_name,
				require_license: this.website.require_license,
				require_registration: this.website.require_registration,
				consent_form: this.website.consent_form,
				contact_form_sms_opt_in: this.website.contact_form_sms_opt_in,
				city: this.website.city,
				state: this.website.state,
				zip_code: this.website.zip_code,
				country: this.website.country,
				contact_email: this.website.contact_email,
				primary_color: this.website.primary_color,
				secondary_color: this.website.secondary_color,
				booking_age: this.website.booking_age,
				sameday_booking: this.website.sameday_booking,
				...(this.favicon ? { favicon: this.favicon } : {}),
				...(this.logo_light ? { logo_light: this.logo_light } : {}),
				...(this.logo_dark ? { logo_dark: this.logo_dark } : {}),
				...(this.og_image ? { og_image: this.og_image } : {}),
				facebook_pixel_id: this.website.facebook_pixel_id || null,
				googleads_customer_id: this.website.googleads_customer_id || null,
				sameday_time: this.website.sameday_time,
				advanced_search: this.website.advanced_search,
				advanced_search_pets: this.website.advanced_search_pets,
				advanced_search_features: this.website.advanced_search_features,
				show_featured_amenities: this.website.show_featured_amenities,
				featured_amenities: this.website.featured_amenities,
				gmap_key: this.website.gmap_key,
				gtm_key: this.website.gtm_key,
				recaptcha_key: this.website.recaptcha_key,
				recaptcha_secret: this.website.recaptcha_secret,
				campaigner_key: this.website.campaigner_key,
				custom_style: this.website.custom_style,
				disallow_bots: this.website.disallow_bots,
				slug: this.website.slug,
				locales: this.website.locales,
				social_media: this.website.social_media,
				affirm_country: this.website.affirm_country,
				affirm_enable: this.website.affirm_enable,
				affirm_private_key: this.website.affirm_private_key,
				affirm_public_key: this.website.affirm_public_key,
			}
		},

		async fetchCurrencies() {
			const currencies = await this.$ravello.request()
				.v(2)
				.to('currencies')
				.get()

			this.currencies = currencies.map(currency => ({
				text: currency.name.en,
				value: currency.code,
			}))
		},
		async fetchResortsDatasource() {
			if (!this.website.enterprise) {
				this.resorts = []
				return
			}

			this.resorts = await this.$ravello.graphql('resorts')
				.parent(this.$ravello.graphql('enterprise', this.website.enterprise))
				.sort('name')
				.datasource()
				.query()
		},

		async onSubmit() {
			this.$store.dispatch('app/startLoading')
			try {
				if (this.isNew) {
					await this.$ravello.request().v(2).to('websites').create(this.getRequestPayload())
				} else {
					await this.$ravello.request().v(2).to('websites').to(this.id)
						.update(this.getRequestPayload())
				}
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', 'Website'
					+ ` successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'websites' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
	},
}
</script>
